import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  forwardRef,
} from "react";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useForm } from "react-hook-form";
import InputAdornment from "@mui/material/InputAdornment";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { round } from "lodash";
import { useSelector, useDispatch } from "react-redux";
import {
  showNotifier,
  setCompletedOrderCount,
} from "../../../store/globalSlice";
import configData from "../../../config.js";
import ProjectStatus from "../CreateProjectStatus";
import TextFieldControl from "../../../components/Inputs/TextFieldControl";
import SelectControl from "../../../components/Inputs/SelectControl";
import CommonDialog from "../../../components/CommonDialog";
import StoreDialogContent from "../StoreDialogContent";
import ModelStoreDialogContent from "../ModelStoreDialogContent";
import { withSnackbar } from "../../../components/SnackbarHOC";
import {
  handleCommonError,
  MemberNoValidation,
  FILL_IN_ORDER_TYPE_ID,
} from "../../../utils";
import useRequest from "../../../hooks/useRequest";
import PermissionsGate from "../../../permissions/PermissionsGate";
import { SCOPES } from "../../../permissions/permissions-map";

const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={2} ref={ref} {...props} />;
});

const numInputs = ["zipCode", "creditLimit"];

function CreateOrder({ snackbarShowMessage }) {
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    reset,
  } = useForm({
    mode: "all",
  });
  const { requestMaker } = useRequest();
  const dispatch = useDispatch();
  const user = useSelector((store) => store.global.user);
  const store = useSelector((store) => store.global.store);
  const { promiseInProgress } = usePromiseTracker();
  const [country, setCountry] = useState([]);
  const [dcNames, setDcNames] = useState([]);
  const [otNames, setOtNames] = useState([]);
  const [ptNames, setPtNames] = useState([]);
  const [planner, setPlanner] = useState([]);
  const [mac, setMac] = useState([]);
  const [projStatus, setProjStatus] = useState(1);
  const [pending, setPending] = useState(false);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(0);
  const [firstError, setFirstError] = useState("");
  const [storeDetails, setStoreDetails] = useState(null);
  const [modelStoreDetails, setModelStoreDetails] = useState(null);
  const [isCRMStore, setIsCRMStore] = useState(true);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [showStoreDialog, setShowStoreDialog] = useState(false);
  const [selectedStoreIndex, setSelectedStoreIndex] = useState(0);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [otherHelperText, setOtherHelperText] = useState({
    country: "",
    distributionCenter: "",
    planner: "",
    mac: "",
  });
  const memberNumberValueWatcher = watch("memberNumber", "");
  const modelStoreValueWatcher = watch("modelStoreId", "");
  const creditLimitValueWatcher = watch("creditLimit", "");
  const orderTypeValueWatcher = watch("orderTypeId", "");

  useEffect(() => {
    if (orderTypeValueWatcher === FILL_IN_ORDER_TYPE_ID) {
      setValue("modelStoreId", "");
    }
  }, [orderTypeValueWatcher, setValue]);

  useEffect(() => {
    const firstError = Object.keys(errors)?.reduce((field, a) => {
      return !!errors[field] ? field : a;
    }, null);
    if (firstError) {
      setFirstError(firstError);
    }
  }, [errors, setFirstError]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    window.location.reload();
    setOpen(false);
  };

  const handleErrorClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  useEffect(() => {
    // Value should be less than 99 million dollar
    if (round(creditLimitValueWatcher) <= 99999999) {
      setValue("creditLimit", creditLimitValueWatcher);
    } else {
      setValue("creditLimit", `${creditLimitValueWatcher}`.substring(0, 8));
    }
  }, [creditLimitValueWatcher, setValue]);

  const restrictAlpha = (e) => {
    let key = e.key;
    if (
      (parseInt(key) >= 0 && parseInt(key) <= 9) ||
      key === "Backspace" ||
      key === "Control" ||
      key === "c" ||
      key === "x" ||
      key === "v" ||
      key === "Copy" ||
      key === "Cut" ||
      key === "Paste" ||
      key === "Tab" ||
      key === "." ||
      key === "-"
    ) {
    } else {
      e.preventDefault();
    }
  };

  useEffect(() => {
    requestMaker({ method: "get", url: configData.GetAllMasterData }).then(
      (res) => {
        setCountry(res.data.countrylist);
        setDcNames(res.data.distributioncenter);
        setOtNames(res.data.orderType);
        setPtNames(res.data.projectType);
        setPlanner(res.data.planner);
        setMac(res.data.Mac);
      }
    );
  }, []);

  const memberNumberError = useMemo(
    () =>
      memberNumberValueWatcher.length < 1 &&
      orderTypeValueWatcher === FILL_IN_ORDER_TYPE_ID,
    [memberNumberValueWatcher, orderTypeValueWatcher]
  );

  const onSubmit = (data) => {
    const memberNumberParts = data.memberNumber?.split("-") || [];
    data.memberNumber = memberNumberParts[0] || null;
    data.checkDigit =
      memberNumberParts.length > 1 ? memberNumberParts[1] : null;
    data.modelStoreId = selectedStoreId;
    const osoCreditLimit = !!data.creditLimit
      ? round(data.creditLimit, 2)
      : null;
    data.statusCode = projStatus;
    data.isPending = Number(pending);
    for (let j = 0; j < numInputs.length; j++) {
      data[numInputs[j]] = parseInt(data[numInputs[j]]);
    }

    for (let i = 0; i < Object.keys(data).length; i++) {
      if (data[Object.keys(data)[i]] === "" || !data[Object.keys(data)[i]]) {
        data[Object.keys(data)[i]] = null;
      }
    }
    data.creditLimit = osoCreditLimit;
    data.createdBy = user.email;
    if (modelStoreValueWatcher && !selectedStoreId) {
      setShowErrorDialog(true);
    } else if (
      memberNumberValueWatcher.length < 1 &&
      data.orderTypeId === FILL_IN_ORDER_TYPE_ID
    ) {
      dispatch(
        showNotifier({
          message:
            "Member Number is mandatory for Order Type - Fill In. Please add a Member Number.",
          severity: "error",
        })
      );
    } else {
      if (!memberNumberError) {
        trackPromise(
          requestMaker({ method: "post", url: configData.CreateOrder, data })
            .then((res) => {
              setOpen(true);
              setStatus(res.status);
            })
            .catch((err) => {
              setOpen(true);
              setStatus(500);
            })
        );
      }
    }
  };

  const updateFormDetails = (details = {}) => {
    const selectedRdcCenter = dcNames.find((center) => {
      const firstChars = details?.new_rdc?.substr(0, 2);
      const valueRegex = new RegExp(firstChars, "i");
      return valueRegex.test(center.ConfigValue);
    });

    const selectedCountry = country.find(
      (countryDetails) => countryDetails.CountryName === details?.new_country
    );

    if (!selectedRdcCenter && details?.new_rdc) {
      setOtherHelperText((prevState) => ({
        ...prevState,
        distributionCenter: `No match found: ${details?.new_rdc}`,
      }));
    }

    if (!selectedCountry && details?.new_country) {
      setOtherHelperText((prevState) => ({
        ...prevState,
        country: `No match found: ${details?.new_country}`,
      }));
    }

    const selectedProjectType = ptNames.find(
      (projectType) => projectType.ConfigValue === details?.new_projecttype
    );

    const selectedPlanner = planner.find(
      (planner) => planner.Name === details?.new_dtvprojectplannerid?.new_name
    );

    if (!selectedPlanner && details?.new_dtvprojectplannerid?.new_name) {
      setOtherHelperText((prevState) => ({
        ...prevState,
        planner: `No match found: ${details?.new_dtvprojectplannerid?.new_name}`,
      }));
    }

    const selectedMac = mac.find(
      (mac) => mac.Name === details?.new_macid?.new_name
    );

    if (!selectedMac && details?.new_macid?.new_name) {
      setOtherHelperText((prevState) => ({
        ...prevState,
        mac: `No match found: ${details?.new_macid?.new_name}`,
      }));
    }

    reset({
      memberNumber: memberNumberValueWatcher,
      storeName: details?.new_storename?.name ?? "",
      contactPerson: details?.new_storecontactname ?? "",
      contactPhone: details?.new_cellphone ?? "",
      city: details?.new_city ?? "",
      state: details?.new_state ?? "",
      zipCode: details?.new_zipcode ?? "",
      orderTypeId: details?.new_ordertype ?? "",
      streetAddress: details?.new_mailingstreet ?? "",
      countryCode: selectedCountry?.CountryId ?? "",
      rdcNumber: selectedRdcCenter?.ConfigKey ?? "",
      storeSize: details?.new_tvhardwaresqft ?? "",
      linealFootage: details?.new_tvlineal ?? "",
      projectTypeId: selectedProjectType?.ConfigKey ?? "",
      assignedPlanner: selectedPlanner?.UserId ?? "",
      assignedMac: selectedMac?.UserId ?? "",
      modelStoreId: modelStoreValueWatcher,
      creditLimit: details?.new_estosoamount ?? "",
    });
  };

  const handleGetCRMDetails = useCallback(() => {
    setOtherHelperText({
      country: "",
      distributionCenter: "",
      planner: "",
      mac: "",
    });
    clearErrors();
    trackPromise(
      requestMaker({
        method: "post",
        url: configData.GetStoreDetails,
        data: { membernumber: String(memberNumberValueWatcher) },
      })
        .then((res) => {
          const availableStores = res?.data ?? [];
          setStoreDetails(availableStores);
          if (!availableStores.length) {
            reset({
              memberNumber: memberNumberValueWatcher,
              storeName: "",
              contactPerson: "",
              contactPhone: "",
              city: "",
              state: "",
              zipCode: "",
              orderTypeId: "",
              streetAddress: "",
              countryCode: "",
              rdcNumber: "",
              storeSize: "",
              linealFootage: "",
              creditLimit: "",
              projectTypeId: "",
              assignedMac: "",
              assignedPlanner: "",
              modelStoreId: modelStoreValueWatcher,
            });
            return snackbarShowMessage(
              `No details found for a given member number: ${memberNumberValueWatcher}. If you are using check digit then please remove it and try again.`,
              "info"
            );
          }

          if (availableStores.length === 1) {
            updateFormDetails(availableStores[selectedStoreIndex]);
          } else {
            setIsCRMStore(true);
            setShowStoreDialog(true);
          }
        })
        .catch((err) => {
          handleCommonError(snackbarShowMessage);
        })
    );
  }, [
    memberNumberValueWatcher,
    modelStoreValueWatcher,
    country,
    otNames,
    dcNames,
    clearErrors,
    reset,
    selectedStoreIndex,
    snackbarShowMessage,
  ]);

  const handleGetModelStoreDetails = useCallback(() => {
    const modelStoreParts = modelStoreValueWatcher?.split("-") || [];
    clearErrors();
    trackPromise(
      requestMaker({
        method: "post",
        url: configData.GetModelStoreDetails,
        data: {
          memberNumber: modelStoreParts[0] || null,
          checkDigit: modelStoreParts.length > 1 ? modelStoreParts[1] : null,
        },
      })
        .then((res) => {
          const availableModelStores = res?.data ?? [];
          setModelStoreDetails(availableModelStores);
          if (!availableModelStores.length) {
            return snackbarShowMessage(
              `No details found for a given model store: ${modelStoreValueWatcher}`,
              "info"
            );
          } else {
            setIsCRMStore(false);
            setShowStoreDialog(true);
          }
        })
        .catch((err) => {
          handleCommonError(snackbarShowMessage);
        })
    );
  }, [
    modelStoreValueWatcher,
    clearErrors,
    reset,
    selectedStoreIndex,
    snackbarShowMessage,
  ]);

  const disableGetCRMDetails = useMemo(
    () =>
      !memberNumberValueWatcher ||
      memberNumberValueWatcher?.toString()?.trim().length === 0 ||
      errors?.memberNumber,
    [memberNumberValueWatcher, errors?.memberNumber]
  );

  const handleStoreSelection = useCallback((storeIndex) => {
    setSelectedStoreIndex(storeIndex);
  }, []);

  const handleStoreDialogCancel = useCallback(() => {
    setSelectedStoreIndex(0);
    setShowStoreDialog(false);
    if (!isCRMStore) {
      setSelectedStoreId(null);
    }
  }, []);

  const handleStoreDialogDone = useCallback(() => {
    if (isCRMStore) {
      updateFormDetails(storeDetails[selectedStoreIndex]);
    } else {
      setSelectedStoreId(modelStoreDetails[selectedStoreIndex]?.StoreId);
    }
    handleStoreDialogCancel();
  }, [
    storeDetails,
    modelStoreDetails,
    handleStoreDialogCancel,
    selectedStoreIndex,
  ]);

  const isModelStoreValid = useMemo(() => {
    if (modelStoreValueWatcher) {
      const regex = MemberNoValidation.pattern.value;
      return (
        regex.test(modelStoreValueWatcher) && modelStoreValueWatcher.length >= 1
      );
    }
    return true;
  }, [modelStoreValueWatcher]);

  const disableGetModelStoreDetails = useMemo(
    () =>
      modelStoreValueWatcher?.toString()?.trim().length < 1 ||
      !isModelStoreValid,
    [modelStoreValueWatcher, isModelStoreValid]
  );

  const modelStoreError = useMemo(
    () => !isModelStoreValid || (modelStoreValueWatcher && !selectedStoreId),
    [isModelStoreValid, modelStoreValueWatcher, selectedStoreId]
  );

  const modelStoreHelperText = useMemo(() => {
    let helperText = "";
    if (!isModelStoreValid) {
      helperText = "Please enter a valid model store number";
    }
    return helperText;
  }, [isModelStoreValid]);

  const handleErrorDialogClose = useCallback(() => {
    setShowErrorDialog(false);
  }, []);

  const handleOrderTypeChange = useCallback(
    (e) => {
      const type = e?.target?.value ?? null;
      if (type === FILL_IN_ORDER_TYPE_ID && !memberNumberValueWatcher) {
        return dispatch(
          showNotifier({
            message: "Member number is required.",
            severity: "error",
          })
        );
      }
      setValue("orderTypeId", type);
    },
    [setValue, dispatch, memberNumberValueWatcher]
  );

  const memberNumberBlurHandler = (e) => {
    if (!memberNumberValueWatcher) {
      return;
    }
    const memberNumberParts =
      String(memberNumberValueWatcher)?.split("-") || [];
    const _memberNumber = memberNumberParts[0] || null;
    const _checkDigit =
      memberNumberParts.length > 1 ? memberNumberParts[1] : null;
    checkCompletedOrderCount(_memberNumber, _checkDigit);
  };

  const checkCompletedOrderCount = (memberNumber, checkDigit) => {
    requestMaker({
      method: "post",
      url: configData.GetCountOfCompletedOrders,
      data: {
        memberNumber,
        checkDigit,
      },
    })
      .then((res) => {
        dispatch(setCompletedOrderCount(res?.data));
        if (
          res?.data === 0 &&
          orderTypeValueWatcher === FILL_IN_ORDER_TYPE_ID
        ) {
          // resetting order type as its already selected as "fill"
          setValue("orderTypeId", "");
        }
      })
      .catch((_err) => {});
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
      <Container maxWidth="xl" sx={{ paddingY: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column" }} marginTop={1}>
          <Typography sx={{ fontWeight: "bold", fontSize: 17 }} component="p">
            Store Information
          </Typography>
          <Paper
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: 3,
              marginY: 1,
            }}
          >
            <Grid container spacing={2}>
              <Grid
                item
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
                xs={12}
                sm={4}
                md={3}
                lg={3}
              >
                <Grid
                  item
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                  }}
                >
                  <Grid item lg={6}>
                    <TextFieldControl
                      name="memberNumber"
                      control={control}
                      disabled={pending}
                      tooltipTitle="Enter Member Number"
                      label="Member Number"
                      required={false}
                      sx={{ width: "98%" }}
                      inputProps={{ maxLength: 7 }}
                      rules={{
                        ...MemberNoValidation,
                      }}
                      error={errors?.memberNumber || memberNumberError}
                      firstError={firstError}
                      overrideHandlers={{
                        onBlur: memberNumberBlurHandler,
                      }}
                      FormHelperTextProps={{
                        sx: {
                          textWrap: "nowrap",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item lg={6} mt={1.5}>
                    <Button
                      size="small"
                      onClick={handleGetCRMDetails}
                      disabled={disableGetCRMDetails || pending}
                    >
                      Get CRM Details
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <TextFieldControl
                  name="storeName"
                  control={control}
                  error={errors?.storeName}
                  firstError={firstError}
                  disabled={pending}
                  rules={{
                    required: {
                      value: true,
                      message: "This field is required.",
                    },
                  }}
                  tooltipTitle="Enter Store Name (Max 100 characters)"
                  label="Store Name"
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <TextFieldControl
                  name="contactPerson"
                  control={control}
                  tooltipTitle="Enter Contact Person Name (Max 100 characters)"
                  label="Contact Person"
                  inputProps={{ maxLength: 100 }}
                  required={false}
                  disabled={pending}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <TextFieldControl
                  name="contactPhone"
                  control={control}
                  tooltipTitle="Enter Phone Number in this format: 000-000-0000"
                  label="Phone"
                  disabled={pending}
                  inputProps={{
                    maxLength: 12,
                    inputMode: "tel",
                  }}
                  required={false}
                  onKeyDown={restrictAlpha}
                  error={errors?.contactPhone}
                  firstError={firstError}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <TextFieldControl
                  name="streetAddress"
                  control={control}
                  tooltipTitle="Enter Store's Street Address (Max 500 characters)"
                  label="Delivery Street Address"
                  disabled={pending}
                  inputProps={{ maxLength: 500 }}
                  required={false}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <TextFieldControl
                  name="city"
                  control={control}
                  error={errors?.city}
                  disabled={pending}
                  rules={{
                    required: {
                      value: true,
                      message: "This field is required.",
                    },
                  }}
                  tooltipTitle="Enter Store's City (Max 100 characters)"
                  label="Delivery City"
                  inputProps={{ maxLength: 100 }}
                  firstError={firstError}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <TextFieldControl
                  name="state"
                  control={control}
                  tooltipTitle="Enter Store's State (Max 100 characters)"
                  label="Delivery State"
                  disabled={pending}
                  inputProps={{ maxLength: 100 }}
                  required={false}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <SelectControl
                  name="countryCode"
                  control={control}
                  label="Country"
                  disabled={pending}
                  optionList={country}
                  required={false}
                  optionKey="CountryId"
                  optionValue="CountryName"
                  helperText={otherHelperText.country}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <TextFieldControl
                  name="zipCode"
                  control={control}
                  tooltipTitle="Enter Store's Zip Code (Max 9 digits)"
                  label="Delivery Zip Code"
                  disabled={pending}
                  inputProps={{ maxLength: 9, inputMode: "numeric" }}
                  onKeyDown={restrictAlpha}
                  required={false}
                  rules={{
                    pattern: {
                      value: /^[0-9\b]+$/,
                      message: "Please enter number upto 9 digits",
                    },
                  }}
                  error={errors?.zipCode}
                  firstError={firstError}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <SelectControl
                  name="rdcNumber"
                  control={control}
                  label="Distribution Center"
                  optionList={dcNames}
                  required={false}
                  disabled={pending}
                  optionKey="ConfigKey"
                  optionValue="ConfigValue"
                  helperText={otherHelperText.distributionCenter}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <SelectControl
                  name="orderTypeId"
                  control={control}
                  label="Order Type"
                  optionList={otNames}
                  required={false}
                  disabled={pending}
                  optionKey="ConfigKey"
                  optionValue="ConfigValue"
                  disabledOptionKey={[
                    store?.completedOrderCount === 0 ? 2 : -1,
                  ]}
                  overrideHandlers={{
                    onChange: handleOrderTypeChange,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <TextFieldControl
                  name="storeSize"
                  control={control}
                  tooltipTitle="Enter Store Size (sq ft)"
                  label="Store Size"
                  disabled={pending}
                  inputProps={{ maxLength: 5 }}
                  onKeyDown={restrictAlpha}
                  required={false}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">sq ft</InputAdornment>
                    ),
                  }}
                  rules={{
                    pattern: {
                      value: /^[0-9]{0,5}$/,
                      message: "Please enter store size of max 5 digits",
                    },
                  }}
                  error={errors?.storeSize}
                  firstError={firstError}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <TextFieldControl
                  name="linealFootage"
                  control={control}
                  tooltipTitle="Enter Lineal Footage (sq ft)"
                  label="Lineal Footage"
                  disabled={pending}
                  inputProps={{ maxLength: 5 }}
                  onKeyDown={restrictAlpha}
                  required={false}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">sq ft</InputAdornment>
                    ),
                  }}
                  rules={{
                    pattern: {
                      value: /^[0-9]{0,5}$/,
                      message: "Please enter lineal footage of max 5 digits",
                    },
                  }}
                  error={errors?.linealFootage}
                  firstError={firstError}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }} marginTop={1}>
          <Typography sx={{ fontWeight: "bold", fontSize: 17 }} component="p">
            Project Information
          </Typography>
          <Paper
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: 3,
              marginY: 1,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <SelectControl
                  name="projectTypeId"
                  control={control}
                  label="Project Type"
                  optionList={ptNames}
                  disabled={pending}
                  optionKey="ConfigKey"
                  optionValue="ConfigValue"
                  error={errors?.projectTypeId}
                  rules={{
                    required: {
                      value: true,
                      message: "This field is required.",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <SelectControl
                  name="assignedPlanner"
                  control={control}
                  label="Project Planner"
                  optionList={planner}
                  optionKey="UserId"
                  optionValue="UserId"
                  optionLabel="Name"
                  disabled={pending}
                  error={errors?.assignedPlanner}
                  rules={{
                    required: {
                      value: true,
                      message: "This field is required.",
                    },
                  }}
                  helperText={otherHelperText.planner}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <SelectControl
                  name="assignedMac"
                  control={control}
                  label="Assigned MAC"
                  optionList={mac}
                  disabled={pending}
                  optionKey="UserId"
                  optionValue="UserId"
                  optionLabel="Name"
                  required={false}
                  helperText={otherHelperText.mac}
                />
              </Grid>
              {orderTypeValueWatcher !== FILL_IN_ORDER_TYPE_ID && (
                <Grid
                  item
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                  xs={12}
                  sm={4}
                  md={3}
                  lg={3}
                >
                  <Grid
                    item
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      alignSelf: "baseline",
                    }}
                  >
                    <Grid item lg={6}>
                      <TextFieldControl
                        name="modelStoreId"
                        control={control}
                        disabled={pending}
                        tooltipTitle="Enter Model Store"
                        label="Model Store"
                        required={false}
                        sx={{ width: "98%" }}
                        inputProps={{ maxLength: 7 }}
                        error={modelStoreError}
                        helperText={modelStoreHelperText}
                        FormHelperTextProps={{
                          sx: {
                            textWrap: "nowrap",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item lg={6} mt={1.5}>
                      <Button
                        size="small"
                        onClick={handleGetModelStoreDetails}
                        disabled={disableGetModelStoreDetails || pending}
                      >
                        Verify & Select
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }} marginTop={1}>
          <Typography sx={{ fontWeight: "bold", fontSize: 17 }} component="p">
            Credit Information
          </Typography>
          <Paper
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: 3,
              marginY: 1,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4} md={3} lg={3}>
                <TextFieldControl
                  name="creditLimit"
                  control={control}
                  label="OSO Credit Limit"
                  tooltipTitle="Enter OSO Credit Limit ($)"
                  disabled={pending}
                  inputProps={{
                    inputMode: "numeric",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  rules={{
                    pattern: {
                      value: /^[1-9]\d*(\.\d+)?$/,
                      message: "Please enter valid amount",
                    },
                  }}
                  required={false}
                  error={errors?.creditLimit}
                  firstError={firstError}
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }} marginTop={1}>
          <Typography sx={{ fontWeight: "bold", fontSize: 17 }} component="p">
            Project Status
          </Typography>
          <Paper
            sx={{
              display: "flex",
              justifyContent: "center",
              paddingTop: 3,
              marginY: 1,
            }}
          >
            <ProjectStatus
              getProjectStatus={(data) => {
                setProjStatus(data);
              }}
              isProjectPending={(data) => {
                setPending(data);
              }}
            />
          </Paper>
        </Box>
        <PermissionsGate scopes={[SCOPES.canCreate]}>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", pt: 2, pb: 2 }}
          >
            <Button
              variant="outlined"
              sx={{ mr: 3, backgroundColor: "#fff" }}
              onClick={() => {
                window.location.reload();
                window.scroll(0, 0);
              }}
            >
              Reset
            </Button>
            <Button variant="contained" type="submit">
              Save
            </Button>
          </Box>
        </PermissionsGate>
      </Container>
      {status === 200 && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={open}
          onClose={handleClose}
        >
          <Snackbar autoHideDuration={8000} open={open} onClose={handleClose}>
            <Alert
              onClose={handleClose}
              severity="success"
              sx={{ width: "100%" }}
            >
              Project is created Successfully!
            </Alert>
          </Snackbar>
        </Backdrop>
      )}
      {status === 500 && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={open}
          onClose={handleErrorClose}
        >
          <Snackbar
            autoHideDuration={8000}
            open={open}
            onClose={handleErrorClose}
          >
            <Alert
              onClose={handleErrorClose}
              severity="error"
              sx={{ width: "100%" }}
            >
              Failed to save the project. Please try again.
            </Alert>
          </Snackbar>
        </Backdrop>
      )}
      {promiseInProgress && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <CommonDialog
        open={showStoreDialog}
        title="Available Stores"
        maxWidth="xl"
        content={
          isCRMStore ? (
            <StoreDialogContent
              rows={storeDetails}
              handleStoreSelection={handleStoreSelection}
            />
          ) : (
            <ModelStoreDialogContent
              rows={modelStoreDetails}
              handleStoreSelection={handleStoreSelection}
            />
          )
        }
        type="cancelApply"
        handleClose={handleStoreDialogCancel}
        handleDone={handleStoreDialogDone}
      />
      <CommonDialog
        open={showErrorDialog}
        title="Verify & Select Model Store"
        maxWidth="sm"
        content={
          "Please click on 'Verify & Select' button after entering a Model Store and select a specific store to clone. You can create/edit the project with model store, only by selecting a specific store."
        }
        type="ok"
        handleClose={handleErrorDialogClose}
      />
    </form>
  );
}

export default withSnackbar(CreateOrder);
